/**
 * ViewpointCaptureService
 *
 * Service for capturing complete viewer state (camera, selection, visibility, etc.)
 * from Autodesk Forge Viewer and packaging it into BCF-compatible format.
 */

import {
  IInitialViewpoint,
  IViewpointCamera,
  IViewpointComponent,
  IComponentDetails,
  IViewpointVisibility,
  IViewpointClippingPlane,
  IViewpointBimFile,
  IViewpointColoring,
} from 'Data/interfaces/Activities/IViewpointData';

/**
 * Interface for URN to filename mapping
 * Used to provide actual model filenames instead of fallback values
 */
export interface IUrnFilenameMap {
  [urn: string]: string;
}

export class ViewpointCaptureService {
  // Static cache for URN to filename mapping
  // This is populated when capturing viewpoints to ensure we use real filenames
  private static urnFilenameMap: IUrnFilenameMap = {};

  /**
   * Set the URN to filename mapping
   * This should be called before capturing viewpoints to ensure correct filenames
   */
  static setUrnFilenameMap(map: IUrnFilenameMap): void {
    this.urnFilenameMap = map;
  }

  /**
   * Main capture function - orchestrates all viewpoint data capture
   */
  static async captureCurrentViewpoint(
    viewer: Autodesk.Viewing.GuiViewer3D,
    urnFilenameMap?: IUrnFilenameMap
  ): Promise<IInitialViewpoint> {
    if (!viewer) {
      throw new Error('Viewer instance is required');
    }

    // Update the map if provided
    if (urnFilenameMap) {
      this.urnFilenameMap = urnFilenameMap;
    }

    try {
      // Capture all viewpoint data in parallel for efficiency
      const [
        snapshotBase64,
        camera,
        selection,
        visibility,
        clippingPlanes,
        bimFiles,
        coloring,
      ] = await Promise.all([
        this.captureSnapshot(viewer),
        this.captureCamera(viewer),
        this.captureSelection(viewer),
        this.captureVisibility(viewer),
        this.captureClippingPlanes(viewer),
        this.captureBimFiles(viewer),
        this.captureColoring(viewer),
      ]);
      
      const viewpoint: IInitialViewpoint = {
        Index: 0,
        SnapshotBase64: snapshotBase64,
        Camera: camera,
        Selection: selection.length > 0 ? selection : undefined,
        Visibility: visibility,
        ClippingPlanes: clippingPlanes.length > 0 ? clippingPlanes : undefined,
        BimFiles: bimFiles.length > 0 ? bimFiles : undefined,
        Coloring: coloring.length > 0 ? coloring : undefined,
      };

      return viewpoint;
    } catch (error) {
      console.error('Error capturing viewpoint:', error);
      throw error;
    }
  }

  private static async captureSnapshot(
    viewer: Autodesk.Viewing.GuiViewer3D
  ): Promise<string> {
    return new Promise((resolve, reject) => {
      try {
        const width = 800;
        const height = 450;

        viewer.getScreenShot(width, height, (blobURL: string) => {
          if (!blobURL) {
            reject(new Error('Failed to generate screenshot'));
            return;
          }

          fetch(blobURL)
            .then((response) => response.blob())
            .then((blob) => {
              const reader = new FileReader();
              reader.onloadend = () => {
                const base64String = reader.result as string;
                // Revoke the blob URL to free memory
                URL.revokeObjectURL(blobURL);
                resolve(base64String);
              };
              reader.onerror = () => {
                URL.revokeObjectURL(blobURL);
                reject(new Error('Failed to convert blob to base64'));
              };
              reader.readAsDataURL(blob);
            })
            .catch((error) => {
              URL.revokeObjectURL(blobURL);
              reject(error);
            });
        });
      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * Task 2.3: Camera State Capture
   * 
   * Captures camera state following BCF 2.1 specification:
   * - Direction must be a UNIT VECTOR (normalized)
   * - ViewPoint is the camera position
   * - For orthographic: ViewToWorldScale stores the zoom level
   * - For perspective: FieldOfView stores the FOV angle
   * 
   * IMPORTANT: Accounts for globalOffset applied to models
   */
  private static async captureCamera(
    viewer: Autodesk.Viewing.GuiViewer3D
  ): Promise<IViewpointCamera> {
    const cam = viewer.getCamera() as any;

    // Get the globalOffset that was applied to the model
    const models = viewer.getAllModels ? viewer.getAllModels() : [viewer.model].filter(Boolean);
    let globalOffset = { x: 0, y: 0, z: 0 };
    const THREE = (window as any).THREE || (Autodesk as any).THREE;
    
    if (models && models.length > 0) {
      const model = models[0];
      const data = model?.getData();
      
      if (data && data.globalOffset) {
        globalOffset.x = data.globalOffset.x || 0;
        globalOffset.y = data.globalOffset.y || 0;
        globalOffset.z = data.globalOffset.z || 0;
      }
    }

    // Adjust camera position back to world coordinates by adding the globalOffset
    const worldPosition = {
      x: cam.position.x + globalOffset.x,
      y: cam.position.y + globalOffset.y,
      z: cam.position.z + globalOffset.z
    };

    // Calculate direction vector from camera position to target
    const dx = cam.target.x - cam.position.x;
    const dy = cam.target.y - cam.position.y;
    const dz = cam.target.z - cam.position.z;
    
    // Normalize the direction vector (BCF requirement: must be unit vector)
    const length = Math.sqrt(dx * dx + dy * dy + dz * dz) || 1;
    
    const isPerspective = cam.isPerspective !== undefined ? cam.isPerspective : true;

    const worldDirection = { X: dx / length, Y: dy / length, Z: dz / length };
    
    // Ensure the up vector is also a normalized unit vector
    const upLength = Math.sqrt(cam.up.x * cam.up.x + cam.up.y * cam.up.y + cam.up.z * cam.up.z) || 1;
    const worldUpVector = { X: cam.up.x / upLength, Y: cam.up.y / upLength, Z: cam.up.z / upLength };

    const camera: IViewpointCamera = {
      Type: isPerspective ? 'Perspective' : 'Orthogonal',
      ViewPoint: {
        X: worldPosition.x,
        Y: worldPosition.y,
        Z: worldPosition.z,
      },
      Direction: worldDirection,
      UpVector: worldUpVector,
      AspectRatio: cam.aspect || 1,
    };

    if (isPerspective) {
      camera.FieldOfView = cam.fov || 45;
    } else {
      // For orthographic cameras, get the zoom level from top/bottom
      let orthoHeight = cam.orthoHeight;
      
      if (!orthoHeight && cam.top !== undefined && cam.bottom !== undefined) {
        // Calculate orthoHeight from top/bottom (this is the correct vertical field height)
        orthoHeight = Math.abs(cam.top - cam.bottom);
      }
      
      if (!orthoHeight) {
        // Fallback: use a default based on model size
        if (models && models.length > 0) {
          const bbox = models[0].getBoundingBox();
          if (bbox) {
            const size = bbox.getSize(new THREE.Vector3());
            orthoHeight = size.length() * 0.5;
          }
        }
        if (!orthoHeight) {
          orthoHeight = 100; // absolute fallback
        }
      }
      
      camera.ViewToWorldScale = orthoHeight;
    }

    return camera;
  }

  /**
   * Task 2.4: Selection & Component Details Capture
   */
  private static async captureSelection(
    viewer: Autodesk.Viewing.GuiViewer3D
  ): Promise<IViewpointComponent[]> {
    const aggregateSelection = viewer.getAggregateSelection();

    if (!aggregateSelection || aggregateSelection.length === 0) {
      return [];
    }

    const selectionPromises: Promise<IViewpointComponent>[] = [];

    for (const selectionSet of aggregateSelection) {
      const model = selectionSet.model;
      const dbIds = selectionSet.selection;

      for (const dbId of dbIds) {
        selectionPromises.push(this.getComponentDetails(dbId, model));
      }
    }

    return Promise.all(selectionPromises);
  }

  /**
   * Helper to get component details including IFC GUID
   */
  private static async getComponentDetails(
    dbId: number,
    model: Autodesk.Viewing.Model
  ): Promise<IViewpointComponent> {
    return new Promise((resolve, reject) => {
      model.getProperties(dbId, (result: Autodesk.Viewing.PropertyResult) => {
        try {
          const componentDetails: IComponentDetails = {
            DbId: String(dbId),
            ModelUrn: model.getData().urn,
            // Use helper method to get proper model name instead of "Unknown Model"
            ModelName: this.getModelName(model),
          };

          const component: IViewpointComponent = {
            ComponentDetails: componentDetails,
            OriginatingSystem: 'Autodesk Forge Viewer',
          };

          // Extract IFC GUID and other properties
          if (result.properties) {
            for (const prop of result.properties) {
              const displayName = prop.displayName?.toLowerCase() || '';

              if (displayName.includes('ifcguid') || displayName.includes('ifc guid')) {
                component.IfcGuid = String(prop.displayValue);
              }
              if (displayName.includes('level') || displayName.includes('layer')) {
                componentDetails.LayerName = String(prop.displayValue);
              }
              if (displayName.includes('category')) {
                componentDetails.CategoryName = String(prop.displayValue);
              }
            }
          }

          // Use dbId as authoringToolId if IFC GUID not found
          component.AuthoringToolId = component.IfcGuid || String(dbId);

          resolve(component);
        } catch (error) {
          reject(error);
        }
      }, reject);
    });
  }

  /**
   * Helper to extract model name from multiple possible sources
   * Tries to get the real model name instead of falling back to "Unknown"
   */
  private static getModelName(model: Autodesk.Viewing.Model): string {
    try {
      const urn = model.getData()?.urn;

      // Source 1: Check our URN to filename map first (most reliable)
      // This is populated from the application's file list in Redux state
      if (urn && this.urnFilenameMap[urn]) {
        return this.urnFilenameMap[urn];
      }

      // Source 2: Try metadata name (Autodesk metadata)
      const metadataName = model.getData()?.metadata?.name;
      if (metadataName && metadataName.trim() !== '') {
        return metadataName;
      }

      // Source 3: Try to get root node name from instance tree (often contains the filename)
      const instanceTree = model.getInstanceTree();
      if (instanceTree) {
        const rootId = instanceTree.getRootId();
        const rootNodeName = instanceTree.getNodeName(rootId);
        if (rootNodeName && rootNodeName.trim() !== '') {
          return rootNodeName;
        }
      }

      // Source 4: Extract filename from URN if available
      if (urn) {
        // URNs often contain the filename - try to extract it
        // Format: urn:adsk.objects:os.object:bucket/filename
        const parts = urn.split('/');
        if (parts.length > 0) {
          const lastPart = parts[parts.length - 1];
          // Remove file extension and clean up the name
          const filename = lastPart.replace(/\.(rvt|ifc|nwd|dwg|pdf)$/i, '');
          if (filename && filename.trim() !== '') {
            return filename;
          }
        }
      }

      // Source 5: Only use fallback if all other methods fail
      // This should rarely happen in production
      return 'Model';
    } catch (error) {
      console.warn('Error extracting model name:', error);
      return 'Model';
    }
  }

  /**
   * Task 2.5: Visibility Capture
   * 
   * Captures visibility state using BCF optimization strategy:
   * - Compares visible vs hidden components
   * - Chooses strategy that results in smallest exceptions list
   * - "Hide Few" strategy: defaultVisibility=true, exceptions=hidden items
   * - "Show Few" strategy: defaultVisibility=false, exceptions=visible items
   */
  private static async captureVisibility(
    viewer: Autodesk.Viewing.GuiViewer3D
  ): Promise<IViewpointVisibility> {
    const visibleModels = viewer.getVisibleModels();

    // Check if spaces are visible (may not be available in all viewers)
    const spacesVisible = false;
    const spaceBoundariesVisible = false;
    const openingsVisible = false;

    // STEP 1: Get ALL leaf node components from visible models
    const allComponents = await this.getAllLeafNodeComponents(visibleModels);

    // STEP 2: Get VISIBLE leaf node components
    const visibleComponents = await this.getVisibleLeafNodeComponents(viewer, visibleModels);

    // STEP 3: Calculate HIDDEN components by finding the difference
    const visibleAuthoringIds = new Set(visibleComponents.map(c => c.AuthoringToolId));
    const hiddenComponents = allComponents.filter(c => !visibleAuthoringIds.has(c.AuthoringToolId));

    // STEP 4: DECIDE which strategy is more efficient
    if (visibleComponents.length <= hiddenComponents.length) {
      // "Show a Few" strategy - more efficient to list visible items
      return {
        DefaultVisibility: false, // Hide everything by default
        Exceptions: visibleComponents, // Then SHOW these exceptions
        SpacesVisible: spacesVisible,
        SpaceBoundariesVisible: spaceBoundariesVisible,
        OpeningsVisible: openingsVisible,
      };
    } else {
      // "Hide a Few" strategy - more efficient to list hidden items
      return {
        DefaultVisibility: true, // Show everything by default
        Exceptions: hiddenComponents, // Then HIDE these exceptions
        SpacesVisible: spacesVisible,
        SpaceBoundariesVisible: spaceBoundariesVisible,
        OpeningsVisible: openingsVisible,
      };
    }
  }

  /**
   * Helper: Get ALL leaf node components from models
   * Used to establish baseline for visibility calculations
   */
  private static async getAllLeafNodeComponents(
    models: Autodesk.Viewing.Model[]
  ): Promise<IViewpointComponent[]> {
    const allComponentsPromises: Promise<IViewpointComponent[]>[] = [];

    for (const model of models) {
      const instanceTree = model.getInstanceTree();

      if (instanceTree) {
        allComponentsPromises.push(
          new Promise((resolve) => {
            const leafNodeIds: number[] = [];
            const rootId = instanceTree.getRootId();

            // Recursively collect ALL leaf nodes (regardless of visibility)
            const collectLeafNodes = (nodeId: number) => {
              const childCount = instanceTree.getChildCount(nodeId);
              
              if (childCount === 0) {
                // It's a leaf node
                leafNodeIds.push(nodeId);
              } else {
                // Has children, traverse them
                instanceTree.enumNodeChildren(nodeId, (childId: number) => {
                  collectLeafNodes(childId);
                }, false);
              }
            };

            collectLeafNodes(rootId);

            // Get component details for all leaf nodes
            const componentPromises = leafNodeIds.map(nodeId => 
              this.getComponentDetails(nodeId, model).catch(error => {
                console.warn(`Error getting component details for node ${nodeId}:`, error);
                return null;
              })
            );

            Promise.all(componentPromises).then(components => {
              resolve(components.filter(c => c !== null) as IViewpointComponent[]);
            });
          })
        );
      }
    }

    const allComponentArrays = await Promise.all(allComponentsPromises);
    return allComponentArrays.flat();
  }

  /**
   * Helper: Get VISIBLE leaf node components from models
   * Only includes components that are currently visible in the viewer
   */
  private static async getVisibleLeafNodeComponents(
    viewer: Autodesk.Viewing.GuiViewer3D,
    models: Autodesk.Viewing.Model[]
  ): Promise<IViewpointComponent[]> {
    const visibleComponentsPromises: Promise<IViewpointComponent[]>[] = [];

    for (const model of models) {
      const instanceTree = model.getInstanceTree();

      if (instanceTree) {
        visibleComponentsPromises.push(
          new Promise((resolve) => {
            const visibleLeafNodeIds: number[] = [];
            const rootId = instanceTree.getRootId();

            // Recursively collect VISIBLE leaf nodes
            const collectVisibleLeafNodes = (nodeId: number) => {
              if (viewer.isNodeVisible(nodeId, model)) {
                const childCount = instanceTree.getChildCount(nodeId);
                
                if (childCount === 0) {
                  // It's a visible leaf node
                  visibleLeafNodeIds.push(nodeId);
                } else {
                  // Has children, check if they're visible
                  instanceTree.enumNodeChildren(nodeId, (childId: number) => {
                    collectVisibleLeafNodes(childId);
                  }, false);
                }
              }
            };

            collectVisibleLeafNodes(rootId);

            // Get component details for all visible leaf nodes
            const componentPromises = visibleLeafNodeIds.map(nodeId =>
              this.getComponentDetails(nodeId, model).catch(error => {
                console.warn(`Error getting component details for visible node ${nodeId}:`, error);
                return null;
              })
            );

            Promise.all(componentPromises).then(components => {
              resolve(components.filter(c => c !== null) as IViewpointComponent[]);
            });
          })
        );
      }
    }

    const visibleComponentArrays = await Promise.all(visibleComponentsPromises);
    return visibleComponentArrays.flat();
  }

  /**
   * Task 2.6: Clipping Plane Capture
   */
  private static async captureClippingPlanes(
    viewer: Autodesk.Viewing.GuiViewer3D
  ): Promise<IViewpointClippingPlane[]> {
    const cutPlanes = viewer.getCutPlanes();

    if (!cutPlanes || cutPlanes.length === 0) {
      return [];
    }

    return cutPlanes.map((plane: any) => {
      // Vector4 represents plane as (A, B, C, D) where Ax + By + Cz + D = 0
      // Normal vector is (A, B, C), constant is D
      const normalX = plane.x;
      const normalY = plane.y;
      const normalZ = plane.z;
      const constant = plane.w;

      // Calculate a point on the plane from normal and constant using scalar multiplication
      const location = {
        X: normalX * -constant,
        Y: normalY * -constant,
        Z: normalZ * -constant,
      };

      return {
        Direction: {
          X: normalX,
          Y: normalY,
          Z: normalZ,
        },
        Location: location,
      };
    });
  }

  /**
   * Task 2.7: BIM Files Capture
   */
  private static async captureBimFiles(
    viewer: Autodesk.Viewing.GuiViewer3D
  ): Promise<IViewpointBimFile[]> {
    const allModels = viewer.getAllModels();

    if (!allModels || allModels.length === 0) {
      return [];
    }

    const visibleModels = viewer.getVisibleModels() || [];

    return allModels.map((model: Autodesk.Viewing.Model) => {
      const data = model.getData();
      const isVisible = visibleModels.includes(model);
      const metadata = data.metadata;

      return {
        // Use helper method to get proper model name instead of "Unknown"
        Filename: this.getModelName(model),
        Reference: data.urn || '',
        IsVisible: isVisible,
        Date: metadata?.datetime ? new Date(metadata.datetime).toISOString() : undefined,
        IfcProject: undefined, // Could be extracted from IFC metadata if available
        IfcSpatialStructureElement: undefined, // Could be extracted from IFC metadata if available
      };
    });
  }

  /**
   * Task 2.8: Coloring Capture
   */
  private static async captureColoring(
    viewer: Autodesk.Viewing.GuiViewer3D
  ): Promise<IViewpointColoring[]> {
    // Note: getThemingColor is not a standard Viewer API method
    // This would require custom implementation via viewer extensions
    // For now, returning empty array as coloring is optional
    // Future enhancement: use viewer.impl.getFragmentMap() to access theming data
    return [];
  }

  /**
   * Helper to convert color vector to ARGB Hex string
   */
  private static vector4ToARGB(color: { x: number; y: number; z: number; w: number }): string {
    const a = Math.round((color.w ?? 1) * 255);
    const r = Math.round((color.x ?? 0) * 255);
    const g = Math.round((color.y ?? 0) * 255);
    const b = Math.round((color.z ?? 0) * 255);

    const toHex = (value: number) => {
      const hex = Math.max(0, Math.min(255, value)).toString(16).toUpperCase();
      return hex.length === 1 ? '0' + hex : hex;
    };

    return `${toHex(a)}${toHex(r)}${toHex(g)}${toHex(b)}`;
  }
}
